import type {AxiosInstance} from 'axios';
import type {
    ChartData,
    CommonListArgs,
    Country,
    CourseShort,
    FirmwareShort,
    GetEntityByIdParams,
    Gps,
    HoleShort,
    IsoDate,
    Nullable,
    PaginatedList,
    PartnerShort,
    SortListArgs,
} from './types.ts';
import {DeviceStatus, DeviceTypeEnum} from '../assets/enums/device-type.emun.ts';
import {SortOrder} from '@assets/enums';
import {prepareOrderingForBackend} from '../assets/helpers/utils/prepare-ordering-for-backend.ts';
import type {CourseHoleDetail} from '@api/courses-api.ts';

export class DevicesApi {
    constructor(private instance: AxiosInstance) {
    }

    public async getDevices(params: DevicesListArgs) {
        return this.instance.get<PaginatedList<DeviceListItem>>('/devices/', {
            params: {
                page: params.page,
                search: params.search,
                ordering: prepareOrderingForBackend(params.sortBy, params.sortDirection),
                state: params.state,
                type: params.type,
                unlinked: params.unlinked,
            },
        }).then(res => res.data);
    };

    public async getDeviceById(params: GetEntityByIdParams) {
        return this.instance.get<DeviceDetail>(`/devices/${params.id}/`).then(res => res.data);
    };

    public async getDeviceGraphsById(params: GetEntityByIdParams) {
        return this.instance.get<DeviceChartsStats>(`/devices/${params.id}/graphs/`).then(res => res.data);
    };

    public async getFirmwares(params: FirmwaresListArgs) {
        return this.instance.get<Firmware[]>('/firmware/', {params}).then(res => res.data);
    };

    public async setFirmwareVersion(params: SetFirmwareVersionParams) {
        return this.instance.post<void>(`/firmware/${params.versionId}/assign/${params.deviceId}`, {params}).then(res => res.data);
    };

}

export type SetFirmwareVersionParams = {
    versionId: string;
    deviceId: string;
}

export type FirmwaresListArgs = {
    // Sort order by date (ASC — oldest first, DESC — newest first)
    order: SortOrder;
}

export type Firmware = {
    id: string
    version: string
    date: IsoDate
    updated_at: IsoDate
    create_at: IsoDate
}


export type DeviceDetail = {
    id: string;
    device_id: Nullable<string>
    type: DeviceTypeEnum
    firmware: Nullable<FirmwareShort>
    last_heartbeat: Nullable<string>
    last_stream_start: Nullable<IsoDate>;
    gps: Gps
    state: DeviceStatus;
    hls_urls: string[]
    partner: Nullable<PartnerShort>
    course: Nullable<CourseShort>
    mac: Nullable<string>;
    holeNumber: Nullable<number>;
    hole: Nullable<CourseHoleDetail>;
}

export type DevicesListArgs = {
    search?: string;
    state?: DeviceStatus;
    type?: DeviceTypeEnum;
    unlinked?: boolean;
} & CommonListArgs & SortListArgs;

export type DeviceListItem = {
    id: string;
    device_id: string;
    type: DeviceTypeEnum;
    state: DeviceStatus;
    mac_address: Nullable<string>;
    country: Nullable<Country>;
    course: Nullable<CourseShort>
    hole: Nullable<HoleShort>;
    gps: Gps;
    battery: Nullable<number>;
    temperature: Nullable<string>;
    last_heartbeat: Nullable<IsoDate>;
    last_stream_start: Nullable<IsoDate>;
    firmware: Nullable<FirmwareShort>
}
export type AccelerationChartData = {
    date: IsoDate
    value: {
        x: number;
        y: number;
        z: number;
    }
};
export type DeviceChartsStats = {
    temperature: ChartData[]
    battery: ChartData[]
    data_transfer: ChartData[]
    acceleration: AccelerationChartData[]
}
